import React from "react";
import { useLocation } from "react-router-dom";
import { trackStorefrontActivity } from "../utils/activityClient";

function pageAction(pathname) {
  if (pathname === "/") return "home_view";
  if (pathname === "/productos") return "catalog_view";
  if (pathname === "/contact") return "contact_view";
  if (pathname === "/carrito") return "cart_view";
  if (pathname === "/checkout") return "checkout_view";
  if (pathname === "/pedido/confirmado") return "order_confirmed";
  if (pathname.startsWith("/pago/")) return "payment_result";
  return "";
}

export default function StorefrontActivityTracker() {
  const location = useLocation();

  React.useEffect(() => {
    const path = `${location.pathname}${location.search || ""}`;
    const action = pageAction(location.pathname);
    const startedAt = Date.now();
    let maxScroll = 0;
    let closed = false;

    const updateScroll = () => {
      const documentHeight = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      maxScroll = Math.max(maxScroll, Math.round((window.scrollY / documentHeight) * 100));
    };
    const closeVisit = () => {
      if (closed) return;
      closed = true;
      updateScroll();
      trackStorefrontActivity("page_exit", {
        path,
        route: location.pathname,
        durationMs: Date.now() - startedAt,
        scrollPercent: Math.min(100, maxScroll),
      }, { dedupeMs: 500, keepalive: true });
    };

    if (action) {
      trackStorefrontActivity(action, {
        path,
        route: location.pathname,
        query: location.search,
        paymentStatus: location.pathname.startsWith("/pago/") ? location.pathname.split("/").pop() : "",
      }, { dedupeMs: 1200 });
    }

    window.addEventListener("scroll", updateScroll, { passive: true });
    window.addEventListener("pagehide", closeVisit);
    return () => {
      window.removeEventListener("scroll", updateScroll);
      window.removeEventListener("pagehide", closeVisit);
      closeVisit();
    };
  }, [location.pathname, location.search]);

  React.useEffect(() => {
    const handleClick = (event) => {
      const link = event.target?.closest?.("a[href]");
      if (!link) return;
      const href = String(link.getAttribute("href") || "").trim();
      if (!href) return;
      const common = {
        href,
        label: link.getAttribute("aria-label") || link.textContent || "Enlace",
        productId: link.dataset?.trackProductId || "",
        productName: link.dataset?.trackProductName || "",
        route: location.pathname,
      };

      if (/^(https?:\/\/)?(wa\.me|api\.whatsapp\.com)|^whatsapp:/i.test(href)) {
        trackStorefrontActivity("whatsapp_click", common, { dedupeMs: 800 });
        return;
      }
      if (/^tel:/i.test(href)) {
        trackStorefrontActivity("phone_click", common, { dedupeMs: 800 });
        return;
      }
      if (/^mailto:/i.test(href)) {
        trackStorefrontActivity("email_click", common, { dedupeMs: 800 });
        return;
      }
      try {
        const target = new URL(href, window.location.href);
        if (target.origin !== window.location.origin) {
          trackStorefrontActivity("external_link_click", { ...common, href: target.href }, { dedupeMs: 800 });
        }
      } catch {
        // Enlaces relativos o inválidos no necesitan seguimiento externo.
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [location.pathname]);

  return null;
}
